export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// In-app routes. Each returns null when the payload is missing the id the route needs,
// so callers can fall back to plain text instead of linking somewhere broken.
// Note the project route takes the Mongo _id, not the per-account project.projectId.
export const clientPath = (client) => (client?.slug ? `/client/${client.slug}` : null);

export const accountPath = (account) => (account?.slug ? `/account/${account.slug}` : null);

export const projectPath = (account, project) =>
    account?.slug && project?._id ? `/account/${account.slug}/project/${project._id}` : null;

const refId = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') return value._id == null ? null : String(value._id);
    return String(value);
};

// objectType 'User' is deliberately absent: on assignment events objectId is the task, not a user.
export const notificationLinkLabel = (href) => {
    if (!href) return null;
    if (href.includes('/project/')) return 'Open project';
    if (href.startsWith('/account/')) return 'Open account';
    if (href.startsWith('/client/')) return 'Open client';
    return 'Open';
};

export const resolveNotificationLink = (notification) => {
    if (!notification) return null;

    const accountSlug = notification.account?.slug || null;
    const projectId =
        notification.objectType === 'Project' ? refId(notification.objectId) : notification.parentType === 'Project' ? refId(notification.parentId) : null;

    if (accountSlug && projectId) return `/account/${accountSlug}/project/${projectId}`;
    if (accountSlug && notification.objectType === 'Account' && refId(notification.account) === refId(notification.objectId)) return `/account/${accountSlug}`;
    if (notification.objectType === 'Client' && notification.client?.slug) return `/client/${notification.client.slug}`;

    return notification.link || null;
};

export const previewOf = (body, max = 140) => {
    const collapsed = String(body || '')
        .replace(/\s+/g, ' ')
        .trim();
    return collapsed.length > max ? `${collapsed.slice(0, max - 1).trimEnd()}…` : collapsed;
};

const SUPPORTED_URL_PROTOCOLS = new Set([
    'http:',
    'https:',
    'mailto:',
    'sms:',
    'tel:',
]);

export function sanitizeUrl(url) {
    try {
        const parsedUrl = new URL(url);
        // eslint-disable-next-line no-script-url
        if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
            return 'about:blank';
        }
    } catch {
        return url;
    }
    return url;
}

const URL_USERINFO = /^([a-z][a-z0-9+.-]*:\/\/)?([^/?#\s]*)@/i;
const NON_WEB_SCHEMES = new Set(['mailto', 'tel', 'sms', 'ftp', 'ftps', 'sftp', 'ssh', 'file', 'data', 'javascript', 'git', 'ws', 'wss', 'http', 'https']);
const HOST_START = /^[\w-]+(\.[\w-]+)*(:\d+)?([/?#]|$)/;

const decodeUrlPart = (value) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

export const splitUrlCredentials = (value) => {
    const match = typeof value === 'string' ? value.match(URL_USERINFO) : null;
    if (!match || (!match[1] && value.includes('://'))) return { url: value, username: '', password: '' };
    const colon = match[2].indexOf(':');
    const rawUser = colon === -1 ? match[2] : match[2].slice(0, colon);
    const rest = value.slice(match[0].length);
    if (!match[1] && (NON_WEB_SCHEMES.has(rawUser.toLowerCase()) || !HOST_START.test(rest))) return { url: value, username: '', password: '' };
    return {
        url: `${match[1] || ''}${rest}`,
        username: decodeUrlPart(rawUser),
        password: colon === -1 ? '' : decodeUrlPart(match[2].slice(colon + 1)),
    };
};

export const urlHasLogin = (value) => {
    if (typeof value !== 'string') return false;
    const scheme = value.match(/^([a-z][a-z0-9+.-]*):(\/\/)?/i);
    if (scheme && !scheme[2] && NON_WEB_SCHEMES.has(scheme[1].toLowerCase())) return false;
    const rest = scheme && scheme[2] ? value.slice(scheme[0].length) : value;
    const authority = rest.split(/[/?#]/, 1)[0];
    if (authority.includes('@')) return true;
    const hostPort = authority.replace(/^\[[^\]]*\]/, '');
    const colon = hostPort.indexOf(':');
    return colon !== -1 && !/^\d*$/.test(hostPort.slice(colon + 1)) && rest.includes('@');
};

export const withUrlCredentials = (href, username, password) => {
    const match = typeof href === 'string' ? href.match(/^([a-z][a-z0-9+.-]*:\/\/)/i) : null;
    if (!match || (!username && !password)) return href;
    const userinfo = password ? `${encodeURIComponent(username || '')}:${encodeURIComponent(password)}` : encodeURIComponent(username);
    return `${match[1]}${userinfo}@${href.slice(match[1].length)}`;
};

const urlRegExp = new RegExp(
    /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/,
);
export function validateUrl(url) {
    return url === 'https://' || urlRegExp.test(url);
}
